import { uniqueIdentifierForNode } from '@ctw/shared/content/document-viewer/_internal/renderers/helpers';
import {
  DOCUMENT_VIEWER_LIST_ITEM_TEST_ID,
  DOCUMENT_VIEWER_LIST_TEST_ID,
} from '@ctw/shared/test/ids';
import { tw } from '@ctw/shared/utils/tailwind';
import type { CdaSection } from '../../parsers/types';
import { ReactNodeRenderer } from './react-node-renderer';

interface ListRendererProps {
  id: string;
  section: CdaSection;
  list: HTMLElement;
}

export const ReactListRenderer = ({ id, section, list }: ListRendererProps) => {
  const listType =
    (list.getAttribute('listType') ?? list.nodeName === 'ol') ? 'ordered' : 'unordered';
  const children = Array.from(
    list.querySelectorAll(':scope > li, :scope > item, :scope > ul, :scope > ol, :scope > list'),
  );

  const listIdentifier = uniqueIdentifierForNode({ section, node: list, context: `${id}-list` });

  if (listType === 'ordered') {
    return (
      <ol
        id={listIdentifier}
        key={listIdentifier}
        className={tw`list-decimal pl-6`}
        data-testid={DOCUMENT_VIEWER_LIST_TEST_ID}
      >
        {children.map((child, index) => {
          const listItemIdentifier = uniqueIdentifierForNode({
            section,
            node: child,
            context: `${id}-list-item`,
            index,
          });

          if (['ul', 'ol', 'list'].includes(child.nodeName)) {
            return (
              <ReactListRenderer
                section={section}
                list={child as HTMLElement}
                id={listItemIdentifier}
                key={listItemIdentifier}
              />
            );
          }

          return (
            <ListItemRenderer
              id={listItemIdentifier}
              key={listItemIdentifier}
              section={section}
              item={child as HTMLElement}
            />
          );
        })}
      </ol>
    );
  }

  // Default to unordered list
  return (
    <ul
      id={listIdentifier}
      key={listIdentifier}
      className={tw`list-disc pl-6`}
      data-testid={DOCUMENT_VIEWER_LIST_TEST_ID}
    >
      {children.map((child, index) => {
        const listItemIdentifier = uniqueIdentifierForNode({
          section,
          node: child,
          context: `${id}-list-item`,
          index,
        });

        if (['ul', 'ol', 'list'].includes(child.nodeName)) {
          return (
            <ReactListRenderer
              section={section}
              list={child as HTMLElement}
              id={listItemIdentifier}
              key={listItemIdentifier}
            />
          );
        }

        return (
          <ListItemRenderer
            id={listItemIdentifier}
            key={listItemIdentifier}
            section={section}
            item={child as HTMLElement}
          />
        );
      })}
    </ul>
  );
};

interface ListItemRendererProps {
  id: string;
  section: CdaSection;
  item: HTMLElement;
}

const ListItemRenderer = ({ id, section, item }: ListItemRendererProps) => {
  return (
    <li
      className={tw`text-background-inverse [&>*:only-of-type]:p-0`}
      data-testid={DOCUMENT_VIEWER_LIST_ITEM_TEST_ID}
    >
      {Array.from(item.childNodes).map((child, index) => {
        const nodeIdentifier = uniqueIdentifierForNode({
          section,
          node: item,
          context: `${id}-list-item-child`,
          index,
        });

        return (
          <ReactNodeRenderer
            id={nodeIdentifier}
            key={nodeIdentifier}
            section={section}
            node={child}
          />
        );
      })}
    </li>
  );
};
